import { Button } from '@/components/Button';
import { Loading } from '@/components/Loading';
import { useCardLinks } from '@/hooks/useCardLinks';
import { GetLinks } from '@/services/linksService';
import { useCardStore } from '@/stores/useCardStore';
import { Link } from '@/types/link';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiPlus } from 'react-icons/fi';
import { CreateCardLink } from './CreateCardLink';
import { DirectLinkToggle } from './DirectLinkToggle';
import { EmptyLinksState } from './EmptyLinksState';
import { UpdateCardLinkListItems } from './UpdateCardLinkListItems';

interface UpdateCardLinksListProps {
  onLinkClick: (linkId: number) => void;
}

export const UpdateCardLinksList = ({
  onLinkClick,
}: UpdateCardLinksListProps) => {
  const { t } = useTranslation();
  const [isCreateLinkOpen, setIsCreateLinkOpen] = useState<boolean>(false);
  const [availableLinks, setAvailableLinks] = useState<Link[]>([]);
  const { card } = useCardStore();
  const {
    links,
    hasDirectLink,
    setHasDirectLink,
    handleUpdateLinks,
    handleToggleDirectLink,
  } = useCardLinks();

  useEffect(() => {
    const getIcons = async () => {
      const { data } = await GetLinks();
      setAvailableLinks(data);
    };
    getIcons();
  }, []);

  const handleCreateLink = () => {
    setIsCreateLinkOpen(true);
  };

  if (!card) return <Loading />;

  // Animation variants for list items
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  if (!links.length) {
    return (
      <motion.div>
        <EmptyLinksState onCreateLink={handleCreateLink} />
        <CreateCardLink
          isOpen={isCreateLinkOpen}
          onClose={() => setIsCreateLinkOpen(false)}
          links={availableLinks}
        />
      </motion.div>
    );
  }

  return (
    <motion.div>
      <motion.div
        className="flex flex-col space-y-2"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="flex flex-col items-center justify-center gap-4 md:flex-row md:justify-start">
          <DirectLinkToggle
            hasDirectLink={hasDirectLink}
            onToggle={handleToggleDirectLink}
          />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            className="flex w-full sm:w-auto"
          >
            <Button
              id="add-card-link-button"
              onClick={handleCreateLink}
              className="w-full bg-linko-light"
            >
              <FiPlus className="size-4" />
              {t('updateCard:links.create')}
            </Button>
          </motion.div>
        </div>

        {/* Links List */}
        <UpdateCardLinkListItems
          links={links}
          onLinkClick={onLinkClick}
          showDirectOption={hasDirectLink}
          updateLinks={handleUpdateLinks}
          setHasDirectLink={setHasDirectLink}
        />
      </motion.div>

      {/* Create Link Modal */}
      <CreateCardLink
        isOpen={isCreateLinkOpen}
        onClose={() => setIsCreateLinkOpen(false)}
        links={availableLinks}
      />
    </motion.div>
  );
};
